/**
 * Builds info-page tokens for the feature-purchase section.
 *
 * The HTML references each offer's cost multiplier by token:
 *   `{feature.super_bonus_buy.multiplier}`  → e.g. "100"
 *   `{feature.bonus_hunt.multiplier}`       → e.g. "1.5"
 *
 * Sourced from `initialSettings.featurePriceMultipliersById` /
 * `featureModeMultipliersById` so the info-page text always matches what
 * the player is actually charged.
 *
 * Only offers with a positive multiplier produce a token. Offers the
 * operator has disabled (multiplier missing or ≤0) are skipped — the
 * matching `<li data-feature="...">` row in the HTML is gated off by the
 * shell's per-offer flag, the offer's id (`super_bonus_buy`,
 * `bonus_hunt_mode`; decision 27), in parallel, so the missing token never
 * surfaces visibly.
 */
export function buildFeaturePurchaseInfoTokens(input: {
  featurePriceMultipliersById: Readonly<Record<string, number>>;
  featureModeMultipliersById: Readonly<Record<string, number>>;
}): Record<string, string> {
  const tokens: Record<string, string> = {};

  const superBonusBuy = input.featurePriceMultipliersById["super_bonus_buy"];
  if (typeof superBonusBuy === "number" && superBonusBuy > 0) {
    tokens["feature.super_bonus_buy.multiplier"] = formatMultiplier(superBonusBuy);
  }

  const bonusHunt = input.featureModeMultipliersById["bonus_hunt_mode"];
  if (typeof bonusHunt === "number" && bonusHunt > 0) {
    tokens["feature.bonus_hunt.multiplier"] = formatMultiplier(bonusHunt);
  }

  return tokens;
}

/**
 * Renders a multiplier number for display. `100` → `"100"`, `1.5` →
 * `"1.5"`. Avoids `Number.toFixed(N)` to preserve the operator's authored
 * precision — e.g. a multiplier of `2` stays `"2"`, not `"2.00"`.
 */
function formatMultiplier(value: number): string {
  return String(value);
}
